import React, { useEffect, useMemo, useState } from 'react';
import {
  describePeriod,
  formatGap,
  formatInt,
  formatMoneyCompact,
  formatPct,
  isNum,
  ordinal,
} from '../format';
import { breadcrumbItems } from '../head';
import { suburbFacts, suburbHeadline, suburbThinNote } from '../narrative';
import { track } from '../../utils/track';
import useSeoDocumentTitle from '../useSeoDocumentTitle';
import GapScale from '../components/GapScale';
import GapTrendChart from '../components/GapTrendChart';
import HeroStat from '../components/HeroStat';
import { AgentRankList, ResultsTable } from '../components/Tables';
import { ForecastPanel, NearbySuburbs, SeoFooter, WatchCta } from '../components/Panels';
import { Breadcrumbs, FreshnessLine, MethodologyNote, Notice, Section, SegmentedControl, StatGrid } from '../components/Primitives';

const TYPE_OPTIONS = [
  { value: 'all', label: 'All homes' },
  { value: 'house', label: 'Houses' },
  { value: 'unit', label: 'Units' },
];

function statsItems(stats) {
  if (!stats) return [];
  return [
    { label: 'Median sale price', value: isNum(stats.median_sale_price) ? formatMoneyCompact(stats.median_sale_price) : null },
    {
      label: 'Sales with a guide',
      value: formatInt(stats.guide_matched_sales),
      note: isNum(stats.total_sales) ? `of ${formatInt(stats.total_sales)} sales` : null,
    },
    {
      label: 'Typical miss',
      value: isNum(stats.guide_error_abs_pct) ? formatPct(stats.guide_error_abs_pct) : null,
      note: 'either direction',
    },
    {
      label: 'Median days on market',
      value: isNum(stats.median_days_on_market) ? formatInt(stats.median_days_on_market) : null,
    },
    {
      label: 'Median price, year on year',
      value: isNum(stats.yoy_median_change_pct) ? formatGap(stats.yoy_median_change_pct) : null,
    },
  ];
}

export default function SuburbPage({ payload }) {
  const [type, setType] = useState('all');
  useSeoDocumentTitle('suburb', payload);
  useEffect(() => {
    track('suburb_view', { slug: payload.slug, indexable: payload.indexable });
  }, [payload.slug, payload.indexable]);

  const hasBoth = Boolean(payload.by_type?.house && payload.by_type?.unit);
  const stats = type === 'all' ? payload.headline : payload.by_type?.[type] || payload.headline;
  const typeLabel = type === 'house' ? 'houses' : type === 'unit' ? 'units' : 'homes';
  const results = useMemo(
    () =>
      type === 'all'
        ? payload.recent_results
        : payload.recent_results.filter((r) => (type === 'unit' ? r.property_type === 'unit' : r.property_type !== 'unit')),
    [payload.recent_results, type],
  );

  const headline = suburbHeadline(payload);
  const facts = suburbFacts(payload);
  const thin = suburbThinNote(payload);
  const period = describePeriod(payload.data_period);
  const rank = payload.rank || {};
  const eyebrow = [payload.lga?.name, payload.postcode ? `NSW ${payload.postcode}` : 'NSW'].filter(Boolean).join(' · ');

  return (
    <main className="re-seo" id="main">
      <div className="re-seo__inner">
        <Breadcrumbs items={breadcrumbItems('suburb', payload)} />

        <header className="re-masthead">
          <p className="re-kicker">{`Suburb profile · ${eyebrow}`}</p>
          <h1>{payload.name}</h1>
          <p className="re-lede">{headline.text}</p>
        </header>

        {thin ? <Notice>{thin}</Notice> : null}

        <section className="re-lead" aria-label="Headline figures">
          <div className="re-lead__primary">
            {hasBoth ? (
              <SegmentedControl label="Property type" options={TYPE_OPTIONS} value={type} onChange={setType} />
            ) : null}
            <HeroStat
              value={stats.guide_gap_pct}
              caption={`Median result against advertised guide, ${typeLabel}, ${period}.`}
              footer={<FreshnessLine period={payload.data_period} count={stats.guide_matched_sales} />}
            />
          </div>
          <div className="re-lead__secondary">
            <GapScale value={stats.guide_gap_pct} distribution={payload.city_distribution} label={payload.name} />
            {rank.guide_gap || rank.guide_gap_in_lga ? (
              <ul className="re-ranks">
                {rank.guide_gap ? (
                  <li>
                    <strong>{ordinal(rank.guide_gap.position)}</strong> of {formatInt(rank.guide_gap.of)} Sydney suburbs for
                    results above guide
                  </li>
                ) : null}
                {rank.guide_gap_in_lga && payload.lga ? (
                  <li>
                    <strong>{ordinal(rank.guide_gap_in_lga.position)}</strong> of {formatInt(rank.guide_gap_in_lga.of)} in{' '}
                    {payload.lga.name}
                  </li>
                ) : null}
                {rank.median_sale_price ? (
                  <li>
                    <strong>{ordinal(rank.median_sale_price.position)}</strong> of {formatInt(rank.median_sale_price.of)} by
                    median price
                  </li>
                ) : null}
              </ul>
            ) : null}
          </div>
        </section>

        <StatGrid items={statsItems(stats)} />

        {facts.length ? (
          <Section id="notable" title={`What stands out in ${payload.name}`} kicker="From the numbers">
            <ul className="re-facts">
              {facts.map((f) => (
                <li key={f.text}>{f.text}</li>
              ))}
            </ul>
          </Section>
        ) : null}

        {/* Month-by-month numbers on a thin sample are noise, so thin pages skip the chart. */}
        {payload.trend?.length && payload.indexable ? (
          <Section id="trend" title="Results against guide, month by month" kicker="Trend">
            <GapTrendChart
              trend={payload.trend}
              title={`${payload.name}: median gap between sale price and advertised guide, by month`}
              description="Above the guide line, homes sold for more than their advertised guide. Below it, for less. The bars underneath show how many sales each month is based on."
            />
          </Section>
        ) : null}

        <Section
          id="results"
          title="Recent results"
          kicker="Sold"
          aside={hasBoth && type !== 'all' ? <span className="re-muted">Showing {typeLabel}</span> : null}
        >
          <ResultsTable
            results={results}
            caption={`Recent ${payload.name} sales with guide and result`}
            from="suburb"
          />
          <MethodologyNote anchor="gap">
            Guide is the advertised price or range. Gap compares the sale price with the middle of the range.
          </MethodologyNote>
        </Section>

        {payload.agents?.length ? (
          <Section id="agents" title={`Agents selling in ${payload.name}`} kicker="Agents">
            <AgentRankList agents={payload.agents} caption={`Agents selling in ${payload.name}`} from="suburb" />
            <MethodologyNote anchor="error">
              Agents with at least a minimum number of sales with a guide here, ordered by typical miss: how far results
              landed from guide, in either direction.
            </MethodologyNote>
          </Section>
        ) : null}

        {payload.forecast ? (
          <Section id="forecast" title="Where prices may head" kicker="Forecast">
            <ForecastPanel forecast={payload.forecast} />
          </Section>
        ) : null}

        {payload.nearby?.length ? (
          <Section id="nearby" title={`Near ${payload.name}`} kicker="Compare">
            <NearbySuburbs items={payload.nearby} from={payload.slug} />
          </Section>
        ) : null}

        <WatchCta name={payload.name} slug={payload.slug} />

        <SeoFooter dataVersion={payload.data_version} />
      </div>
    </main>
  );
}
